import { useIntl, type UserUKselfCategorisationUserProvidedValues } from '@capsicum/common';
import { RadioButtonInput, TextInput } from '@capsicum/common-web';
import {
	type UkSelfCategorisationFormProps,
	type UkSelfCategorisationRestrictedInvestorFormType,
	useUkSelfCategorisationRestrictedInvestor
} from '@capsicum/features/regulations/shared';
import { useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import styled from 'styled-components';

import { UserInformationForm } from './UserInformationForm';

export const RestrictedInvestorForm = ({
	onFormValidChange,
	onFormValuesChange,
	requirement
}: UkSelfCategorisationFormProps) => {
	const [userInformation, setUserInformation] = useState<null | UserUKselfCategorisationUserProvidedValues>(null);
	const handleUserInformationChange = (values: UserUKselfCategorisationUserProvidedValues) => {
		setUserInformation(values);
	};

	const { $t } = useIntl();

	const {
		isRestrictedFormValid,
		restrictedHasInvestedLessThanTenPercent,
		restrictedHasInvestedPercentValue,
		restrictedWillInvestLessThanTenPercent,
		restrictedWillInvestPercentValue,
		setUserHasInvestedPercentage,
		setUserWillInvestPercentage,
		ukSelfCategorisationRestrictedInvestorForm,
		userHasInvestedLessOptions,
		userWillInvestLessOptions
	} = useUkSelfCategorisationRestrictedInvestor();

	useEffect(() => {
		onFormValidChange(isRestrictedFormValid);
	}, [isRestrictedFormValid, onFormValidChange, restrictedHasInvestedPercentValue]);

	useEffect(() => {
		const newRestrictedFormValues: UserUKselfCategorisationUserProvidedValues = {
			category: 'restricted',
			firstName: userInformation?.firstName,
			lastName: userInformation?.lastName,
			restrictedHasInvestedLessThanTenPercent: restrictedHasInvestedLessThanTenPercent,
			restrictedHasInvestedPercent: parseFloat(restrictedHasInvestedPercentValue),
			restrictedWillInvestLessThanTenPercent: restrictedWillInvestLessThanTenPercent,
			restrictedWillInvestPercent: parseFloat(restrictedWillInvestPercentValue)
		};

		onFormValuesChange(newRestrictedFormValues);
	}, [
		onFormValuesChange,
		restrictedHasInvestedPercentValue,
		restrictedHasInvestedLessThanTenPercent,
		restrictedWillInvestLessThanTenPercent,
		restrictedWillInvestPercentValue,
		ukSelfCategorisationRestrictedInvestorForm,
		userInformation
	]);

	return (
		<FormProvider {...ukSelfCategorisationRestrictedInvestorForm}>
			<S.Intro>
				<S.Description>{$t('domesticRinggitBorrowing.selectedLabel')}</S.Description>
				<S.DescriptionBold>
					{$t('jurisdictionRequirements.ukSelfCategorisation.restrictedInvestorTitlePage')}
				</S.DescriptionBold>
			</S.Intro>
			<S.Paragraph>{$t('jurisdictionRequirements.ukSelfCategorisation.restristedInvestorText5')}</S.Paragraph>
			<S.Paragraph>{$t('jurisdictionRequirements.ukSelfCategorisation.restristedInvestorText6')}</S.Paragraph>
			<S.RadioButtonsContainer>
				{userHasInvestedLessOptions.map(item => (
					<RadioButtonInput
						checked={item.id === restrictedHasInvestedLessThanTenPercent}
						item={item}
						key={item.id.toString()}
						pressableLabels
						testID={
							item.id
								? 'restricted-has-invested-less-than-ten-percent'
								: 'restricted-has-invested-more-than-ten-percent'
						}
						onPress={() => setUserHasInvestedPercentage(item.id)}
					/>
				))}
			</S.RadioButtonsContainer>
			<S.Paragraph>{$t('jurisdictionRequirements.ukSelfCategorisation.restristedInvestorText7')}</S.Paragraph>
			<TextInput<UkSelfCategorisationRestrictedInvestorFormType>
				disabled={!restrictedHasInvestedLessThanTenPercent}
				label={$t('jurisdictionRequirements.ukSelfCategorisation.percentage')}
				name="restrictedHasInvestedPercent"
				supportingText={$t('jurisdictionRequirements.ukSelfCategorisation.upToTwoDecimals')}
				testID="restricted-investor-percentage-has-invested-input"
				type="text"
			/>
			<S.Paragraph>{$t('jurisdictionRequirements.ukSelfCategorisation.and')}</S.Paragraph>
			<S.Paragraph>{$t('jurisdictionRequirements.ukSelfCategorisation.restristedInvestorText8')}</S.Paragraph>
			<S.RadioButtonsContainer>
				{userWillInvestLessOptions.map(item => (
					<RadioButtonInput
						checked={item.id === restrictedWillInvestLessThanTenPercent}
						item={item}
						key={item.id.toString()}
						pressableLabels
						testID={
							item.id
								? 'restricted-will-invest-less-than-ten-percent'
								: 'restricted-will-not-invest-more-than-ten-percent'
						}
						onPress={() => setUserWillInvestPercentage(item.id)}
					/>
				))}
			</S.RadioButtonsContainer>
			<S.Paragraph>{$t('jurisdictionRequirements.ukSelfCategorisation.restristedInvestorText9')}</S.Paragraph>
			<TextInput<UkSelfCategorisationRestrictedInvestorFormType>
				disabled={!restrictedWillInvestLessThanTenPercent}
				label={$t('jurisdictionRequirements.ukSelfCategorisation.percentage')}
				name="restrictedWillInvestPercent"
				supportingText={$t('jurisdictionRequirements.ukSelfCategorisation.upToTwoDecimals')}
				testID="restricted-investor-percentage-will-invest-input"
				type="text"
			/>
			<S.Paragraph>{$t('jurisdictionRequirements.ukSelfCategorisation.restristedInvestorText10')}</S.Paragraph>
			<UserInformationForm requirement={requirement} onUserInformationFormChange={handleUserInformationChange} />
		</FormProvider>
	);
};

namespace S {
	export const Intro = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const Paragraph = styled.p`
		${({ theme }) => theme.fonts.text.text3};
	`;

	export const RadioButtonsContainer = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.large};
		margin: ${({ theme }) => `${theme.spacings.small} 0`};
	`;

	export const Description = styled.span`
		${({ theme }) => theme.fonts.text.text3};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const DescriptionBold = styled.span`
		${({ theme }) => theme.fonts.headings.title5};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		display: block;
	`;
}
